import type { KeyboardEvent } from 'react';
import { useCallback } from 'react';

import { ENTER_KEY, SPACE_KEY } from '@luna/a11y';

/**
 * A custom React hook that creates a keyboard event handler for Enter and Space key presses.
 *
 * This hook is helpful for creating accessible custom components or adding keyboard navigation
 * to complex UI elements.
 *
 * @param {() => void} callback - The function to be called when Enter or Space key is pressed.
 * @returns {(event: KeyboardEvent<HTMLElement>) => void} A keyboard event handler function.
 *
 * @example
 * // Real-world usage in a thread card component
 * function ThreadCard({ id, flags, detailHref, onThreadClick }) {
 *   const navigate = useNavigate();
 *
 *   const handleThreadCardClick = useCallback((): void => {
 *     georgeStats.trackEvent(trackingCategory, 'show_thread', { threadId: id });
 *     onThreadClick?.(flags, id);
 *     navigate(detailHref, { absolute: true });
 *   }, [id, navigate, detailHref, onThreadClick]);
 *
 *   const handleEnterThreadWithKeyboard = useEnterOrSpaceAsClick(handleThreadCardClick);
 *
 *   return (
 *     <div
 *       role="button"
 *       onClick={handleThreadCardClick}
 *       onKeyDown={handleEnterThreadWithKeyboard}
 *     >
 *     {content}
 *     </div>
 *   );
 * }
 *
 */
export const useEnterOrSpaceAsClick = (
    callback: () => void,
): ((event: KeyboardEvent<HTMLElement>) => void) =>
    useCallback(
        (event: KeyboardEvent<HTMLElement>): void => {
            if (event.key === ENTER_KEY || event.key === SPACE_KEY) {
                callback();
                event.preventDefault();
            }
        },
        [callback],
    );
